import React from 'react';

const FourFaces = () => {
  const faces = [
    {
      title: 'Anchor Chart Guy',
      emoji: '⚓',
      tagline: 'The steady hand when everything else is melting down.',
      description: '"Been here since the last three \'deaths of crypto.\' Still here." The one who remembers when a 30% day was normal. Anchor Chart Guy has the battle scars and the wisdom to know that survival beats profits. They\'ve evolved past FOMO into something more dangerous: patience.',
      traits: [
        'Talks in frameworks: trend, structure, risk, invalidation.',
        'Doesn\'t chase every move, waits for their spots.',
        'Explains the "why," not just the "what."',
        'Comfortable saying "no trade" or "I don\'t know."',
        'Keeps some kind of journal or stats, even if it\'s messy.'
      ],
      bestAt: 'Turning chaos into a checklist.',
      watchOut: 'Getting ignored until things are already on fire.',
      quote: '"I\'d rather be consistently boring than occasionally wrecked."'
    },
    {
      title: 'Apprentice Chart Guy',
      emoji: '🌱',
      tagline: 'Hungry to learn, trying not to repeat the same mistakes.',
      description: 'Apprentice Chart Guy is in the thick of it, learning patterns, testing strategies, and trying to make sense of everyone yelling on Crypto Twitter. They might not have it all figured out yet, but they\'re asking the right questions.',
      traits: [
        'Screenshot folders full of charts and notes.',
        'Bounces between styles: scalper one week, swing trader the next.',
        'Loves clear, step-by-step breakdowns from more experienced traders.',
        'Occasionally tilts into FOMO, then spends the weekend studying.',
        'Starting to care more about risk than "one big win."'
      ],
      bestAt: 'Bringing curiosity and fresh eyes to old ideas.',
      watchOut: 'Switching strategies so often nothing has time to work.',
      quote: '"Okay…but can you show me exactly how you\'d trade this?"'
    },
    {
      title: 'Matrix Chart Guy',
      emoji: '💊',
      tagline: 'The pattern-spotter with ten charts open and one brain in hyperfocus.',
      description: '"The one who sees the code behind the candles." Matrix Chart Guy doesn\'t just read charts, they see the underlying patterns that others miss. While everyone else sees random price action, they\'re tracking correlations across timeframes, spotting the glitches in market behavior, and building systems to decode it all.',
      traits: [
        'Monitors a ridiculous number of tickers and timeframes.',
        'Spots rotations, liquidity grabs, and fakeouts early.',
        'Loves building watchlists, indicators, or custom scripts.',
        'Can hyperfocus for hours and forget to actually place the trade.',
        'Has three "final" versions of every idea.'
      ],
      bestAt: 'Catching subtle shifts in market behavior before they\'re obvious.',
      watchOut: 'Analysis paralysis and over-engineering simple setups.',
      quote: '"Something about this order flow feels off…give it a day."'
    },
    {
      title: 'Degen Chart Guy',
      emoji: '🎲',
      tagline: 'Here for the ride, the rush, and the story the chart tells.',
      description: '"Every candle has a heartbeat if you listen close enough." Living proof that you can be both technical and emotional. They trade with their whole chest, turning market moves into poetry. Sure, they might overtrade, but they\'ll never miss the story.',
      traits: [
        'Loves memes, low caps, and big swings.',
        'Uses charts to justify gut feelings more than frameworks.',
        'Posts PnL screenshots and midnight cope tweets in equal measure.',
        'Follows good accounts, but doesn\'t always follow their risk rules.',
        'The first to shout "SEND IT" when volume kicks in.'
      ],
      bestAt: 'Sensing sentiment and momentum in real time.',
      watchOut: 'Letting emotions push them into trades their plan never agreed to.',
      quote: '"If I\'m not slightly nauseous, is it even a trade?"'
    }
  ];

  return (
    <section className="section-container bg-gradient-to-b from-chartguy-dark to-black" data-testid="four-faces-section">
      {/* Header */}
      <div className="text-center mb-12 max-w-4xl mx-auto">
        <h2 className="section-title gradient-text" data-testid="four-faces-title">
          The Four Faces of Chart Guy
        </h2>
        <p className="text-chartguy-yellow text-lg mb-4 font-semibold">
          Every chart watcher lives somewhere on this map.
        </p>
        <p className="body-text text-gray-300 mb-2">
          Chart Guy isn't one person. He's a spectrum of habits, quirks, and battle scars shared by anyone who stares at candles a little too long.
        </p>
        <p className="body-text text-gray-300 mb-2">
          To make it simple, we break the entire universe of charters into four archetypes. None of them are "right" or "wrong," they're just different ways of showing up to the same game.
        </p>
        <p className="text-gray-400 text-sm italic">
          Find the version of Chart Guy that feels most like you today. (You can always evolve into another one later.)
        </p>
      </div>

      {/* 2x2 Grid of Cards */}
      <div className="archetype-grid max-w-6xl mx-auto">
        {faces.map((face, index) => (
          <div 
            key={index} 
            className="archetype-card"
            data-testid={`face-card-${index}`}
          >
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              <div className="text-5xl">{face.emoji}</div>
              <div className="flex-1">
                <h3 className="card-title text-chartguy-yellow">
                  {face.title}
                </h3>
                <p className="text-gray-400 text-sm italic">
                  {face.tagline}
                </p>
              </div>
            </div>

            {/* Description */}
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              {face.description}
            </p>

            {/* Traits */}
            <div className="mb-4">
              <h4 className="text-chartguy-yellow font-semibold text-xs uppercase tracking-wide mb-2">Traits:</h4>
              <ul className="traits-list space-y-1.5">
                {face.traits.map((trait, i) => (
                  <li key={i} className="text-gray-400 text-xs flex items-start">
                    <span className="text-chartguy-yellow mr-2 flex-shrink-0">•</span>
                    <span>{trait}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Best At & Watch Out */}
            <div className="space-y-2 mb-4 pb-4 border-b border-chartguy-yellow/20">
              <div className="flex items-start gap-2">
                <span className="text-chartguy-yellow font-semibold text-xs flex-shrink-0">Best At:</span>
                <span className="text-gray-300 text-xs">{face.bestAt}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-chartguy-brown font-semibold text-xs flex-shrink-0">Watch Out:</span>
                <span className="text-gray-400 text-xs">{face.watchOut}</span>
              </div>
            </div>

            {/* Quote */}
            <p className="text-gray-400 text-xs italic text-center">
              {face.quote}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FourFaces;

